
import { mapGetters, mapState } from "vuex";

import { PAGE_TYPE, CART_PAGE_QUERY } from "./-index.query";
import { seoMixin, gtmEventMixin, pageMixin } from "~/mixins";

const UID = "bag";

export default {
  mixins: [seoMixin, gtmEventMixin, pageMixin],

  async asyncData({ app, store }) {
    const isCartEmpty = store.state.bag.miniCart.lineItemsCount === 0;
    await store.dispatch("quickship/init");

    const [{ [PAGE_TYPE]: data }] = await Promise.all([
      app.$prismicGql.request({
        query: CART_PAGE_QUERY,
        variables: { uid: UID, isCartEmpty, lazy: false },
      }),
      store.dispatch("bag/fetchAssemblyVariants"),
      store.dispatch("app/fetchMattressAndBaseRemoval"),
    ]);

    data.show_donation && store.dispatch("bag/showDonation");

    store.dispatch("bag/SET_OFFER_DATA", {
      showOffer: data.show_offer,
      offerTags: data.offer_tags?.split?.(",") ?? [],
    });

    return {
      uid: UID,
      pageType: PAGE_TYPE,
      slices: data.slices,
      title: data.title,
      description: data.description,
      checkoutStyles: {
        color: data.checkout_color,
        backgroundColor: data.checkout_background_color,
        fontSize: data.checkout_font + "px",
      },
      checkoutButtonText: data.checkout_text,
      metaTitle: data.metaTitle,
      metaDescription: data.metaDescription,
      metaRobots: data.metaRobots,
      metaFields: { _linkType: data._linkType, _meta: data._meta },
      paymentIcons: data.payment_icon_document,
      checkoutStickyText: data.checkout_sticky_text[0].text,
    };
  },

  data: () => ({ showDesktopFooter: false, showMobileFooter: false }),

  async fetch() {
    await this.$store.dispatch("bag/fetchShopMetafields");
  },

  fetchOnServer: false,

  computed: {
    ...mapState(["siteConfig"]),
    ...mapState("bag", ["miniCart"]),
    ...mapGetters("bag", ["visibleLineItems", "lines"]),
    checkoutOffset() {
      return this.siteConfig.headerHeight / 2;
    },
  },
  watch: {
    visibleLineItems(items, prevItems) {
      if (items.length !== 0) {
        return;
      }

      if (prevItems.length === 0 && items.length === 0) {
        return;
      }

      // Remove the invisible items from the cart
      if (this.lines.length !== 0) {
        const payload = this.lines.map(item => item.id);
        this.$store.dispatch("bag/REMOVE_CART_LINES", payload);
      }

      // Refresh the whole page to get more content which is visible only after cart gets empty
      console.log("Going for refresh");
      this.$nuxt.refresh();
    },
  },

  created() {
    this.gtmCheckoutEvent(this.visibleLineItems);
  },

  mounted() {
    if (this.miniCart.lineItemsCount) {
      this.gtmViewCart();
    }
    this.$searchspring.trackBasketView();
  },

  methods: {
    gtmViewCart() {
      this.$gtm.gtag("event", "view_cart", {
        value: this.miniCart.totalPrice,
        coupon: this.lines.reduce((acc, item) => {
          if (item.discountAllocations.length) {
            acc.push(
              item?.discountAllocations?.[0]?.discountApplication?.title +
                " - " +
                item?.discountAllocations?.[0]?.discountApplication?.value
                  ?.amount
            );
          }

          return acc;
        }, []),
        items: this.lines.reduce((acc, item) => {
          acc.push({
            item_sku: item?.variant?.sku,
            item_id: item?.variant?.product?.id,
            item_name: item?.variant?.product?.title,
            coupon: item.discountAllocations.length
              ? item?.discountAllocations?.[0]?.discountApplication?.title +
                " - " +
                item?.discountAllocations?.[0]?.discountApplication?.value
                  ?.amount
              : "",
            currency: "AUD",
            item_brand: item?.variant?.product?.brand_list ?? "",
            item_category: item?.variant?.product?.product_category ?? "",
            item_variant: item?.variant?.title,
            quantity: item?.quantity,
            price: item?.variant?.price,
            ...(item.variant.compareAtPrice > item.variant.price && {
              discount: item.variant.compareAtPrice - item.variant.price,
            }),
          });

          return acc;
        }, []),
      });
    },
    catchCheckoutScroll(event, el) {
      this.showDesktopFooter =
        window.pageYOffset > el.offsetTop + this.checkoutOffset;
      let rect = el.getBoundingClientRect();
      this.showMobileFooter =
        rect.top >= 0 && rect.bottom <= window.innerHeight;
    },
  },
};
